<template>
	<el-dialog
		:title="title"
		:visible.sync="visible"
		:before-close="handleClose"
		:width="'520px'">

		<template v-if="contact && contact.origin !== 'RISOLTO'">
			<el-alert
				:title="$t('warning')"
				type="info"
				:description="$t('ContactDialog.are_you_sure')"
				show-icon>
			</el-alert>

			<el-checkbox v-model="form.copy_to_risolto" class="mt-5"> {{ $t('ContactDialog.copy_to_risolto') }} </el-checkbox>
		</template>

		<el-form ref="form" class="mt-20" :model="form" :rules="rules">
			<el-form-item :label="$t('ContactDialog.firstName')" prop="first_name">
				<el-input v-model="form.first_name" :placeholder="$t('ContactDialog.firstName')" :disabled="disabled"></el-input>
			</el-form-item>

			<el-form-item :label="$t('ContactDialog.lastName')" prop="last_name">
				<el-input v-model="form.last_name" :placeholder="$t('ContactDialog.lastName')" :disabled="disabled"></el-input>
			</el-form-item>

			<el-form-item :label="$t('ContactDialog.locale')" prop="locale">
				<el-select v-model="form.language" :placeholder="$t('ContactDialog.locale')" :disabled="disabled">
					<el-option
						v-for="item in languages"
						:key="item"
						:label="$translate.language(item)"
						:value="item">
					</el-option>
				</el-select>
			</el-form-item>

			<el-form-item :label="$t('ContactDialog.contactType')" prop="contact_type">
				<el-select v-model="form.contact_type" :disabled="sortedContactTypes.length == 0" clearable :placeholder="$t('ContactDialog.contactType')">
					<el-option
						v-for="contact_type in sortedContactTypes"
						:key="contact_type.reference"
						:label="contact_type.contact_type"
						:value="contact_type.reference">
					</el-option>
				</el-select>
			</el-form-item>
		</el-form>
		<span slot="footer" class="dialog-footer">
			<el-button @click="handleClose">{{ $t("shared.Cancel") }}</el-button>
			<el-button :loading="loading" type="primary" @click="handleFormSubmit">{{ $t("shared.Confirm") }}</el-button>
		</span>
	</el-dialog>
</template>
<script>
import { LANGUAGES } from '@/constants';
import axios from 'axios';
import { recipientUpdateContact } from '@/api/recipient';
import gql from 'graphql-tag';

const GET_CONTACT_TYPES = gql`
	query ($participant: uuid!) {
		contact_types: risolto_contact_type (where: {
			_and: [
			{
				_or: [
					{ participant: { _eq: $participant } },
					{ participant: { _is_null: true } },
				]
			}
			]
		}) {
			reference
			contact_type
		}
	}`;

export default {
	apollo: {
		contact_types: {
			query: GET_CONTACT_TYPES,
			variables() {
				return {
					'participant': this.participant
				};
			}
		}
	},
	props: {
		visible: { type: Boolean, required: true},
		participant: { type: String, required: true },
		legalEntity: { type: String, required: true },
		recipient: { type: String, required: true },
		contact: { type: Object, required: false, default: () => null },
		dialogClose: { type: Function, required: false, default: undefined }
	},
	data() {
		return {
			loading: false,
			form: {
				reference: null,
				copy_to_risolto: false,
				contact_type: null,
				first_name: '',
				last_name: '',
				locale: 'nl'
			},
			rules: {
				first_name: [
					{ required: true, message: this.$t('ContactDialog.firstNameRequired'), trigger: 'blur' }
				],
				last_name: [
					{ required: true, message: this.$t('ContactDialog.lastNameRequired'), trigger: 'blur' }
				]
			},
			languages: LANGUAGES
		};
	},
	computed: {
		title() {
			return this.contact ? this.$t('ContactDialog.editContact') : this.$t('ContactDialog.addContact');
		},
		sortedContactTypes() {
			return [...this.contact_types || []].sort((a, b) => {
				return a.contact_type.localeCompare(b.contact_type);
			});
		},
		disabled() {
			return !this.form.copy_to_risolto && this.contact?.reference != null && this.contact?.origin !== 'RISOLTO';
		}
	},
	watch: {
		'contact': {
			handler(newVal, oldVal) {
				if (newVal) this.populateFormForEdit(newVal);
				else this.form = this.getDefaultFormState();
			},
			immediate: true,
			deep: true
		}
	},
	methods: {
		getDefaultFormState() {
			return {
				reference: null,
				copy_to_risolto: false,
				contact_type: null,
				first_name: '',
				last_name: '',
				language: 'nl'
			};
		},

		populateFormForEdit(contact) {
			this.form = {
				...this.getDefaultFormState,
				reference: contact.reference,
				contact_type: contact.contact_type_by_reference?.reference,
				first_name: contact.first_name,
				last_name: contact.last_name,
				language: contact.locale.substring(0, 2)
			};
		},

		handleClose() {
			if (this.dialogClose) {
				this.dialogClose();
			}

			this.handleReset();
		},

		handleReset() {
			this.$refs['form'].resetFields();
			this.form = this.getDefaultFormState();
		},

		handleFormSubmit() {
			this.$refs['form'].validate((valid) => {
				if (valid) {
					if (this.contact && this.form.copy_to_risolto) {
						this.createContact();
					} else if (this.contact) {
						this.updateContact();
					} else {
						this.createContact();
					}
				} else {
					return false;
				}
			});
		},

		async createContact() {
			if (this.loading) {
				return;
			}

			this.loading = true;
			const payload = {
				...this.form,
				participant: this.participant,
				legal_entity: this.legalEntity,
				recipient: this.recipient
			};

			try {
				await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/contact`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				this.handleClose();
				this.$store.commit('apollo/RISOLTO_CONTACT');
			} catch (e) {
				//
			} finally {
				this.loading = false;
			}
		},

		async updateContact() {
			if (this.loading) {
				return;
			}

			let contact = {
				...this.form,
				participant: this.participant,
				legal_entity: this.legalEntity,
				recipient: this.recipient
			};

			await recipientUpdateContact(
				this.participant,
				this.legalEntity,
				this.recipient,
				contact
			);

			this.loading = false;

			this.handleClose();
			this.$store.commit('apollo/RISOLTO_CONTACT');
		}
	}
};
</script>
<style lang="scss" scoped>
.el-form {
	width: 480px;
}

.el-select {
	width: 100%;
}
</style>
